// Auth-check helpers — the two flavors of session-check the
// ServiceLayerAuthProvider runs:
//   1. After an OAuth callback (URL had `?auth=success`) — wait for
//      cookies to be available, retry up to 5 times with linear
//      backoff, only show an error on definitive 401.
//   2. On normal mount — check session, attempt re-handoff if
//      cookies have expired but Supabase still has a session, fall
//      through to clearing state only on confirmed 401 + we
//      previously had an email.

import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { isServiceLayerApiError } from '@appic/service-layer'
import { isSessionExpiredCode } from '../http/errorEnvelope'
import { attemptReHandoff } from './authReHandoff'

type SetState<T> = Dispatch<SetStateAction<T>>

/**
 * Whether a caught error means the SESSION is genuinely gone (401 /
 * session-expiry) — the only signal that may drive `auth:session-expired` and
 * clear auth state.
 *
 * STRUCTURED check, not a substring match. `fetchSession` -> `apiJson` throws a
 * typed {@link ServiceLayerApiError} carrying `.status` (and an envelope
 * `.code`), so we branch on `status === 401` / a session-expiry `code`. A 403
 * FORBIDDEN is authorization denial on a VALID session and must NOT trip this
 * (the 2026-06-01 all-dark regression, #1793). The previous
 * `error.message.includes('UNAUTHORIZED')` was fragile: a renamed/translated
 * gateway message silently changed this behaviour
 * (BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31).
 *
 * Falls back to the legacy message heuristic ONLY for the
 * `fetchSession`-synthesised "Session response missing user information" (a
 * plain Error with no status) and other untyped errors, preserving today's
 * "missing user => treat as auth error" behaviour.
 */
function isSessionGoneError(error: unknown): boolean {
  if (isServiceLayerApiError(error)) {
    if (error.status === 401) return true
    if (error.status === 403) return false // FORBIDDEN: valid session, denied.
    return typeof error.code === 'string' && isSessionExpiredCode(error.code)
  }
  // Untyped errors (e.g. the synthesised "missing user information", or any
  // non-HTTP throw). Preserve the prior heuristic for these only.
  return error instanceof Error && (
    error.message.includes('Not authenticated') ||
    error.message.includes('401') ||
    error.message.includes('UNAUTHORIZED')
  )
}

export interface AuthCheckDeps {
  signal?: AbortSignal
  safeFetchSession: (signal?: AbortSignal) => Promise<unknown>
  setAuthError: SetState<string | null>
  setLastErrorCode: SetState<string | null>
  setLoading: SetState<boolean>
  setReady: SetState<boolean>
  setEmail: SetState<string | null>
  setRole: SetState<string | null>
  setCsrfToken: SetState<string | null>
}

// OAuth callback success path. Caller already cleared the URL query
// params and the OAuth attempt record before invoking this.
export async function runOAuthCallbackCheck(deps: AuthCheckDeps): Promise<void> {
  const { signal, safeFetchSession, setAuthError, setLastErrorCode, setLoading, setReady } = deps
  if (signal?.aborted) return
  setLoading(true)

  // Production cross-site cookies (SameSite=None) take longer to
  // be available than local same-origin cookies — retry with
  // 1s..5s linear backoff.
  const maxRetries = 5
  const baseDelay = 1000
  let lastError: Error | null = null
  let lastStatus: number | null = null

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (signal?.aborted) return
    const delay = baseDelay * (attempt + 1)
    if (attempt > 0) {
      console.warn(`[ServiceLayerAuth] Retrying session check (attempt ${attempt + 1}/${maxRetries}) after ${delay}ms delay...`)
    } else {
      console.warn('[ServiceLayerAuth] Waiting 1s for cookies to be available before first session check...')
    }
    await new Promise((resolve) => setTimeout(resolve, delay))
    if (signal?.aborted) return

    try {
      await safeFetchSession(signal)
      if (!signal?.aborted) {
        setAuthError(null)
        setLastErrorCode(null)
        sessionStorage.removeItem('oauth_attempt_ts')
        console.warn('[ServiceLayerAuth] Session verified successfully after OAuth callback')
        break
      }
    } catch (error) {
      if (signal?.aborted) return
      lastError = error instanceof Error ? error : new Error(String(error))
      const statusMatch = lastError.message.match(/status[:\s]+(\d+)/i)
      if (statusMatch) lastStatus = parseInt(statusMatch[1], 10)
      console.warn(`[ServiceLayerAuth] Session check failed (attempt ${attempt + 1}/${maxRetries}):`, {
        error: lastError.message, status: lastStatus, attempt: attempt + 1, maxRetries,
      })
      if (attempt === maxRetries - 1) {
        // Only surface error on definitive 401; transient
        // network/timeout issues let the next interaction retry.
        if (lastStatus === 401) {
          setAuthError('Authentication verification failed. Please try refreshing the page.')
          setLastErrorCode('session_check_failed')
        } else {
          console.warn('[ServiceLayerAuth] Session check failed but might be network issue, not showing error')
          setAuthError(null)
        }
      }
    }
  }

  if (!signal?.aborted) {
    setLoading(false)
    setReady(true)
  }
}

interface MountCheckDeps extends AuthCheckDeps {
  ready: boolean
  emailRef: MutableRefObject<string | null>
}

// Normal-mount session check. Skips the loading spinner if we
// already had an email (prevents flicker on navigation).
export async function runMountSessionCheck(deps: MountCheckDeps): Promise<void> {
  const {
    signal,
    safeFetchSession,
    ready,
    emailRef,
    setAuthError,
    setLastErrorCode,
    setLoading,
    setReady,
    setEmail,
    setRole,
    setCsrfToken,
  } = deps
  if (signal?.aborted) return

  const hadEmail = emailRef.current !== null
  if (!hadEmail) setLoading(true)

  // Small delay on initial mount so cookies are available after
  // navigation (race-condition guard).
  if (!ready && !hadEmail) {
    await new Promise((resolve) => setTimeout(resolve, 150))
    if (signal?.aborted) return
  }

  try {
    await safeFetchSession(signal)
    if (!signal?.aborted) {
      setAuthError(null)
      setLastErrorCode(null)
      sessionStorage.removeItem('oauth_attempt_ts')
    }
  } catch (error) {
    if (signal?.aborted) return

    const isAuthError = isSessionGoneError(error)

    if (isAuthError) {
      // Try re-handoff before clearing state — Supabase might
      // still have a valid session even though SL cookies
      // expired.
      const handed = await attemptReHandoff({ hadEmail, signal })
      if (handed) {
        try {
          await safeFetchSession(signal)
          if (!signal?.aborted) {
            setAuthError(null)
            setLastErrorCode(null)
          }
          return
        } catch (postHandoffError) {
          // Cookie-handoff returned OK but the follow-up session
          // fetch still failed (cookie not yet persisted, transient
          // 5xx). Fall through to the cleanup so stale auth state
          // isn't preserved.
          if (signal?.aborted) return
          console.warn(
            '[ServiceLayerAuth] Post-handoff session fetch failed:',
            postHandoffError instanceof Error ? postHandoffError.message : postHandoffError,
          )
        }
      }
      // The caller aborted while the re-handoff ran (unmount / superseded check): it no longer owns this
      // auth state, so it must neither announce a session expiry nor clear the identity (Codex #2213).
      if (signal?.aborted) return

      if (hadEmail) {
        console.warn('[ServiceLayerAuth] Session expired or invalid, clearing auth state')
        try {
          window.dispatchEvent(new CustomEvent('auth:session-expired', {
            detail: { email: null, timestamp: new Date().toISOString() },
          }))
        } catch {
          // audit event - never block auth
        }
        setEmail(null)
        setRole(null)
        setCsrfToken(null)
      }
    } else {
      // Transient (network / 500 / etc.) — preserve email if we
      // had one to avoid flicker on navigation.
      if (!hadEmail) {
        setEmail(null)
        setCsrfToken(null)
      }
      console.warn(
        '[ServiceLayerAuth] Transient error during auth check, preserving state:',
        error instanceof Error ? error.message : 'Unknown error',
      )
    }

    if (error instanceof Error && isAuthError) {
      console.warn('[ServiceLayerAuth] Auth check failed (expected if not logged in):', error.message, {
        auth_flow_id: null,
        request_id: null,
      })
    }
  } finally {
    if (!signal?.aborted) {
      if (!hadEmail) setLoading(false)
      setReady(true)
    }
  }
}
